import { useState } from 'react';
import { Group, Paper, Select, SegmentedControl, Stack, Text } from '@mantine/core';
import { LineChart } from '@mantine/charts';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import {
  aggregateActiveCompanies,
  aggregatePlatformTotals,
  METRIC_OPTIONS,
  MetricOption,
  Period,
  UsageMetricKey,
  UsageMetricRow,
} from './aggregate';

const METRIC_LABEL_BY_VALUE = Object.fromEntries(METRIC_OPTIONS.map(o => [o.value, o.label]));

export default function TotalsPanel({
  rows,
  extraTotalKeys,
}: {
  rows: UsageMetricRow[];
  extraTotalKeys: readonly UsageMetricKey[];
}) {
  const [period, setPeriod] = useState<Period>('month');
  const [metric, setMetric] = useState<MetricOption>('total');

  const points = aggregatePlatformTotals(rows, period, extraTotalKeys);
  const seriesLabel = METRIC_LABEL_BY_VALUE[metric] ?? metric;
  const chartData = points.map(point => ({
    period: point.periodLabel,
    [seriesLabel]: metric === 'total' ? point.total : point.totals[metric],
  }));

  const activePoints = aggregateActiveCompanies(rows, period, extraTotalKeys);
  const activeChartData = activePoints.map(point => ({
    period: point.periodLabel,
    'Active companies': point.activeCompanies,
    'Companies with over 10 actions': point.highActivityCompanies,
  }));

  return (
    <Stack gap="lg">
      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Group justify="space-between" align="center" wrap="wrap">
            <Text size="sm" fw={FONT_WEIGHTS.semibold}>
              Platform usage over time
            </Text>
            <Group gap="xs">
              <Select
                size="xs"
                w={190}
                value={metric}
                onChange={value => setMetric((value as MetricOption | null) ?? 'total')}
                data={METRIC_OPTIONS}
                allowDeselect={false}
              />
              <SegmentedControl
                size="xs"
                value={period}
                onChange={value => setPeriod(value as Period)}
                data={[
                  { value: 'week', label: 'Week' },
                  { value: 'month', label: 'Month' },
                ]}
              />
            </Group>
          </Group>
          {chartData.length === 0 ? (
            <Text size="sm" c="dimmed">
              No usage recorded yet for this filter.
            </Text>
          ) : (
            <LineChart
              h={300}
              data={chartData}
              dataKey="period"
              series={[{ name: seriesLabel, color: 'brand.6' }]}
              curveType="monotone"
              withDots
              withLegend
              gridAxis="x"
            />
          )}
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text size="sm" fw={FONT_WEIGHTS.semibold}>
            Active companies
          </Text>
          {activeChartData.length === 0 ? (
            <Text size="sm" c="dimmed">
              No usage recorded yet for this filter.
            </Text>
          ) : (
            <LineChart
              h={300}
              data={activeChartData}
              dataKey="period"
              series={[
                { name: 'Active companies', color: 'blue.6' },
                { name: 'Companies with over 10 actions', color: 'orange.6' },
              ]}
              curveType="monotone"
              withDots
              withLegend
              gridAxis="x"
            />
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
